"use client";

import Link from "next/link";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { Dialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import type { LearnPart } from "@/features/learn/api";

/**
 * The track's syllabus, reachable from the reader's top bar (`LN-RDR01`,
 * docs/35 §5).
 *
 * It is the shared `Dialog` and not a bespoke drawer: this app has one
 * accessible overlay and everything with a backdrop is on it (`EX-QA02`,
 * `AGENTS.md` rule 17). The rows are the same syllabus the track page draws,
 * from the same payload, so there is nothing here that could disagree with it.
 *
 * A lesson that carries no reader address (`href` still pointing at the course
 * player) is listed and linked exactly as the track page lists it, because the
 * backend already decided which of the two links a lesson gets.
 */
export function LearnSyllabusPanel({
  open,
  onClose,
  parts,
  locale,
  currentLessonId,
  completedLessonIds,
}: {
  open: boolean;
  onClose: () => void;
  parts: LearnPart[];
  locale: Locale;
  currentLessonId: string;
  completedLessonIds: readonly string[];
}) {
  const t = useTranslations("learn");
  const tr = useTranslations("learn.reader");
  const done = new Set(completedLessonIds);

  return (
    <Dialog
      className="max-w-2xl"
      closeLabel={tr("closeSyllabus")}
      onClose={onClose}
      open={open}
      title={t("syllabusHeading")}
    >
      <div className="max-h-[70vh] space-y-8 overflow-y-auto pr-1">
        {parts.map((part) => (
          <section aria-labelledby={`reader-part-${part.key}`} key={part.key}>
            <h3
              className="font-[family-name:var(--font-heading)] text-sm font-semibold uppercase tracking-wide text-primary"
              id={`reader-part-${part.key}`}
            >
              {locale === "bn" && part.title_bn ? part.title_bn : part.title_en}
            </h3>
            <ol className="mt-3 space-y-4">
              {part.modules.map((module) => (
                <li key={module.id}>
                  <p className="text-sm font-semibold text-[rgb(var(--foreground))]">
                    {locale === "bn" && module.title_bn ? module.title_bn : module.title_en}
                  </p>
                  <ol className="mt-1.5 space-y-1">
                    {module.lessons.map((lesson) => {
                      const isCurrent = lesson.id === currentLessonId;
                      return (
                        <li className="flex items-center gap-2" key={lesson.id}>
                          <span
                            aria-hidden
                            className={cn(
                              "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border",
                              done.has(lesson.id)
                                ? "border-primary bg-primary text-[rgb(var(--primary-foreground))]"
                                : "border-[rgb(var(--border))]",
                            )}
                          >
                            {done.has(lesson.id) ? <Check className="h-2.5 w-2.5" /> : null}
                          </span>
                          <Link
                            aria-current={isCurrent ? "page" : undefined}
                            className={cn(
                              "min-w-0 flex-1 truncate text-sm hover:text-primary hover:underline",
                              isCurrent
                                ? "font-semibold text-primary"
                                : "text-[rgb(var(--muted-foreground))]",
                            )}
                            href={`/${locale}${lesson.href}`}
                            onClick={onClose}
                          >
                            {locale === "bn" && lesson.title_bn
                              ? lesson.title_bn
                              : lesson.title_en}
                          </Link>
                        </li>
                      );
                    })}
                  </ol>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
